@if (facade.organization(); as organization) {
  <div class="d-flex flex-column gap-4" data-testid="overview-panel">
    <section class="d-flex flex-column gap-2">
      <h3 class="av-section-title m-0">{{ 'backoffice.organizations.overview.identity_title' | translate }}</h3>

      <dl class="detail-grid m-0">
        <dt class="av-meta">{{ 'backoffice.organizations.overview.facts.name' | translate }}</dt>
        <dd data-testid="overview-name">{{ organization.name || unresolved }}</dd>

        <dt class="av-meta">{{ 'backoffice.organizations.overview.facts.domain' | translate }}</dt>
        <dd>{{ organization.domain || unresolved }}</dd>

        <dt class="av-meta">{{ 'backoffice.organizations.overview.facts.nickname' | translate }}</dt>
        <dd>{{ organization.avyna_nickname || unresolved }}</dd>

        <dt class="av-meta">{{ 'backoffice.organizations.overview.facts.contact_email' | translate }}</dt>
        <dd>{{ organization.contact_email || unresolved }}</dd>

        <dt class="av-meta">{{ 'backoffice.organizations.overview.facts.market' | translate }}</dt>
        <dd>{{ organization.market?.name || organization.market?.code || unresolved }}</dd>

        <dt class="av-meta">{{ 'backoffice.organizations.overview.facts.currency' | translate }}</dt>
        <dd>{{ organization.currency || unresolved }}</dd>

        <dt class="av-meta">{{ 'backoffice.organizations.overview.facts.root_user' | translate }}</dt>
        <dd data-testid="overview-root">{{ organization.rootUserName }}</dd>

        <dt class="av-meta">{{ 'backoffice.organizations.overview.facts.created' | translate }}</dt>
        <dd>
          {{ organization.created_at ? (organization.created_at | date: dateTimeFormat) : unresolved }}
        </dd>
      </dl>
    </section>

    <section class="d-flex flex-column gap-2">
      <h3 class="av-section-title m-0">{{ 'backoffice.organizations.overview.tenant_title' | translate }}</h3>

      <dl class="detail-grid m-0">
        <dt class="av-meta">{{ 'backoffice.organizations.overview.facts.entra_tenant' | translate }}</dt>
        <dd class="av-source" data-testid="overview-tenant">
          {{ organization.entra_tenant_id || unresolved }}
        </dd>

        <dt class="av-meta">{{ 'backoffice.organizations.overview.facts.admin_consent' | translate }}</dt>
        <dd>
          {{
            organization.admin_consent_granted_at
              ? (organization.admin_consent_granted_at | date: dateTimeFormat)
              : ('backoffice.organizations.overview.consent_missing' | translate)
          }}
        </dd>

        <dt class="av-meta">{{ 'backoffice.organizations.overview.facts.installed' | translate }}</dt>
        <dd>
          {{
            organization.installed_at ? (organization.installed_at | date: dateTimeFormat) : unresolved
          }}
        </dd>

        <dt class="av-meta">{{ 'backoffice.organizations.overview.facts.stripe_customer' | translate }}</dt>
        <dd class="av-source">{{ organization.stripe_customer_id || unresolved }}</dd>

        <dt class="av-meta">{{ 'backoffice.organizations.overview.facts.tenant_health' | translate }}</dt>
        <dd [matTooltip]="'backoffice.organizations.overview.tenant_health_note' | translate">{{ organization.tenant_health_status || unresolved }}</dd>
      </dl>
    </section>

    <section class="d-flex flex-column gap-2" data-testid="billing-email-panel">
      <h3 class="av-section-title m-0">{{ 'backoffice.organizations.overview.billing_email_title' | translate }}</h3>

      <div class="d-flex flex-wrap align-items-center gap-2">
        <span class="cell-strong" data-testid="billing-email-value">
          {{ organization.billing_email || unresolved }}
        </span>

        <av-status-chip
          [label]="billingEmailSourceLabelKey() | translate"
          [tone]="hasOverride() ? 'positive' : 'neutral'"
        ></av-status-chip>

        @if (organization.billing_email_is_inherited) {
          <span class="av-meta" data-testid="billing-email-inherited">{{ 'backoffice.organizations.overview.billing_email.inherited_hint' | translate }}</span>
        }
      </div>

      <p class="av-meta m-0" data-testid="billing-email-cascade-note">{{ 'backoffice.organizations.overview.billing_email.cascade_note' | translate }}</p>

      @if (facade.canEdit()) {
        <form [formGroup]="formGroup" class="d-flex flex-wrap align-items-end gap-2">
          <label class="d-flex flex-column gap-1 flex-grow-1" for="billing-email">
            <span class="av-meta">{{ 'backoffice.organizations.overview.billing_email.override_label' | translate }}</span>
            <input
              id="billing-email"
              type="email"
              class="av-field w-100"
              formControlName="billing_email"
              [placeholder]="'backoffice.organizations.overview.billing_email.override_placeholder' | translate"
            />
          </label>

          <button
            type="button"
            class="av-btn av-btn-primary"
            [disabled]="!formGroup.valid || facade.isMutating()"
            (click)="saveBillingEmail()"
          >
            {{ 'backoffice.organizations.overview.billing_email.save' | translate }}
          </button>

          @if (hasOverride()) {
            <button
              type="button"
              class="av-btn"
              [disabled]="facade.isMutating()"
              (click)="clearOverride()"
            >
              {{ 'backoffice.organizations.overview.billing_email.clear' | translate }}
            </button>
          }
        </form>
      }
    </section>

    <section class="d-flex flex-column gap-2" data-testid="lifecycle-panel">
      <h3 class="av-section-title m-0">{{ 'backoffice.organizations.overview.lifecycle_title' | translate }}</h3>

      <div class="d-flex flex-wrap align-items-center gap-2">
        <av-status-chip
          [label]="organization.statusLabelKey | translate"
          [tone]="organization.statusTone"
        ></av-status-chip>
      </div>

      @if (facade.isTerminal()) {
        <div class="av-banner p-3 d-flex align-items-start gap-2" data-testid="lifecycle-terminal">
          <mat-icon aria-hidden="true">block</mat-icon>
          <span>{{ 'backoffice.organizations.overview.lifecycle.terminal_note' | translate }}</span>
        </div>
      } @else if (!facade.canChangeLifecycle()) {
        <p class="av-meta m-0" data-testid="lifecycle-restricted">{{ 'backoffice.organizations.overview.lifecycle.restricted' | translate }}</p>
      } @else {
        <p class="av-section-hint m-0" data-testid="lifecycle-consequence">
          {{
            (facade.isSuspended()
              ? 'backoffice.organizations.overview.lifecycle.reactivate_consequence'
              : 'backoffice.organizations.overview.lifecycle.suspend_consequence'
            ) | translate
          }}
        </p>

        <div class="d-flex gap-2">
          @if (facade.isSuspended()) {
            <button
              type="button"
              class="av-btn av-btn-primary"
              [disabled]="facade.isMutating()"
              (click)="reactivate()"
            >
              <mat-icon aria-hidden="true">play_arrow</mat-icon>
              {{ 'backoffice.organizations.overview.lifecycle.reactivate' | translate }}
            </button>
          } @else {
            <button
              type="button"
              class="av-btn av-btn-danger"
              [disabled]="facade.isMutating()"
              (click)="openSuspend()"
            >
              <mat-icon aria-hidden="true">pause</mat-icon>
              {{ 'backoffice.organizations.overview.lifecycle.suspend' | translate }}
            </button>
          }
        </div>
      }
    </section>
  </div>
}
